import { useNavigate, useParams } from 'react-router-dom';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import ConversionFactorImportLineService from '../../../../shared/services/conversion-factor-import-line/conversion-factor-import-line.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { LoadingButton } from '@mui/lab';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import CategoryService from '../../../../shared/services/category/category.service';
import { DeleteOutlined, SaveOutlined } from '@mui/icons-material';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { findCategoryByText } from '../../../../shared/utils/category-utils';

export const SuperAdminConversionFactorsImportingPage = () => {
  const { year, conversionSourceId } = useParams();
  const navigate = useNavigate();
  //Services
  const categoryService = new CategoryService();
  const conversionFactorImportLineService = new ConversionFactorImportLineService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [conversionSource, setConversionSource] = useState<IConversionSource>()
  const [tab, setTab] = useState(0);
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [levelOnes, setLevelOnes] = useState<string[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  //Table State
  const [records, setRecords] = useState<any[]>();
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>();
  const [change, setChange] = useState<number>(0)
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isCommitting, setIsCommitting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  //Variables
  const allMapped = levelOnes.length > 0 && levelOnes.every(l => !!mappings[l]);

  useEffect(() => {
    if (tab === 0) {
      getRecords('pending');
    } else if (tab === 1) {
      getRecords('committed');
    } else if (tab === 2) {
      getRecords('discarded');
    }
  }, [inputPage, perPage, change]);


  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [tab, order, orderBy, year, conversionSourceId])

  useEffect(() => {
    if (!conversionSourceId || !year) return;
    categoryService.getAllCategories('?includeHidden=true')
      .then(response => {
        setCategories(response || []);
      })
      .catch((error) => {
        console.log(error)
        setCategories([]);
      });
  }, [conversionSourceId, year]);

  const getRecords = (status: string) => {
    conversionFactorImportLineService.getAllWithPagination(inputPage, perPage, orderBy, order, `&conversionSourceId=${conversionSourceId}&year=${year}&status=${status}`)
      .then(response => {
        setRecords(response.rows);
        setTotalRecords(response.count);
        setConversionSource(response.conversionSource)
        if (status === 'pending' && response.count === 0) {
          if (response.importFailed) {
            userNotificationsContext.openSnackbar({ open: true, message: 'The import failed and no rows were staged. Check the column mapping matches the file, then delete this year and re-upload.', backgroundColor: 'text.primary' });
            navigate(`/super-admin/conversion-factors/${conversionSourceId}`);
          } else {
            navigate(`/super-admin/conversion-factors/${conversionSourceId}/${year}/committed`);
          }
        }
      })
      .catch((error) => {
        console.log(error)
        setRecords([]);
      });
  }

  const headCells: IHeadCell[] = [
    { id: 'scope', numeric: false, label: 'Scope', sortable: false },
    { id: 'detailLevelOne', numeric: false, label: 'Level One', sortable: true },
    { id: 'detailLevelTwo', numeric: false, label: 'Level Two', sortable: true },
    { id: 'detailLevelThree', numeric: false, label: 'Level Three', sortable: true },
    { id: 'detailLevelFour', numeric: false, label: 'Level Four', sortable: true },
    { id: 'detailLevelFive', numeric: false, label: 'Level Five', sortable: true },
    { id: 'measurementUnitName', numeric: false, label: 'Unit', sortable: true },
    { id: 'greenHouseGasUnitName', numeric: false, label: 'GHG Unit', sortable: true },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
  ];
  const headCellsPending: IHeadCell[] = [
    ...headCells,
    { id: 'failureReason', numeric: false, label: 'Reason (if any)', sortable: false },
  ];

  const rows = !records ? [] : records.map((record) => {
    return {
      id: record.id,
      scope: record.scope,
      detailLevelOne: record.detailLevelOne,
      detailLevelTwo: record.detailLevelTwo,
      detailLevelThree: record.detailLevelThree,
      detailLevelFour: record.detailLevelFour,
      detailLevelFive: record.detailLevelFive,
      measurementUnitName: record.measurementUnitName,
      greenHouseGasUnitName: record.greenHouseGasUnitName,
      multiplier: record.multiplier,
      failureReason: record.failureReason,
    };
  });

  const headCellsSpendBased: IHeadCell[] = [
    { id: 'sourceRecordKey', numeric: false, label: 'Code', sortable: false },
    { id: 'activity', numeric: false, label: 'Activity', sortable: false },
    { id: 'measurementUnitName', numeric: false, label: 'Unit', sortable: true },
    { id: 'greenhouseGasUnitName', numeric: false, label: 'GHG Unit', sortable: true },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
  ];
  const headCellsPendingSpendBased: IHeadCell[] = [
    ...headCellsSpendBased,
    { id: 'failureReason', numeric: false, label: 'Reason (if any)', sortable: false },
  ];

  const rowsSpendBased = !records ? [] : records.map((record) => {
    return {
      id: record.id,
      sourceRecordKey: record.sourceRecordKey,
      activity: record.activity,
      measurementUnitName: record.measurementUnitName,
      greenhouseGasUnitName: record.greenhouseGasUnitName,
      multiplier: record.multiplier,
    };
  });

  //Conversion only
  const mapCategoryHandler = () => {
    conversionFactorImportLineService.getLevelOnes(`?conversionSourceId=${conversionSourceId}&year=${year}`)
      .then(response => {
        const values = (response.values ?? []) as any[];
        const items = values.map(v => typeof v === 'string' ? { levelOne: v, categoryId: null } : v);
        const lvlOnes = [...new Set(items.map(i => i.levelOne))];
        setLevelOnes(lvlOnes);
        const next: Record<string, string> = {};
        items.forEach(i => { if (i.categoryId) next[i.levelOne] = i.categoryId; });
        items.forEach(i => {
          if (!next[i.levelOne]) {
            const match = findCategoryByText(categories, i.levelOne);
            if (match) next[i.levelOne] = match.id;
          }
        });
        setMappings(next);
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => setIsMappingOpen(true));
  }


  //Conversion only
  const saveDialogHandler = () => {
    if (!allMapped) return;
    conversionFactorImportLineService.postCategoryMappings({ conversionSourceId, year, mappings })
      .then(() => {
        setIsMappingOpen(false);
        setMappings({});
        setTab(0);
        getRecords('pending');
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
      })
      .catch((error) => {
        console.log(error)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      })
  }

  const commitHandler = () => {
    if (conversionSource && conversionSource.conversionType === 'conversion') {
      conversionFactorImportLineService.getLevelOnes(`?conversionSourceId=${conversionSourceId}&year=${year}`)
        .then(response => {
          const values = (response.values ?? []) as any[];
          const items = values.map(v => typeof v === 'string' ? { levelOne: v, categoryId: null } : v);
          const missing = items.filter(i => !i.categoryId);
          if (missing.length > 0) {
            const lvlOnes = [...new Set(items.map(i => i.levelOne))];
            setLevelOnes(lvlOnes);
            const next: Record<string, string> = {};
            items.forEach(i => { if (i.categoryId) next[i.levelOne] = i.categoryId; });
            items.forEach(i => {
              if (!next[i.levelOne]) {
                const match = findCategoryByText(categories, i.levelOne);
                if (match) next[i.levelOne] = match.id;
              }
            });
            setMappings(next);
            setIsMappingOpen(true);
            return;
          }
          commitLines()
        });
    } else if (conversionSource && conversionSource.conversionType === 'spendBasedConversion') {
      commitLines()
    }
  }

  const commitLines = () => {
    setIsCommitting(true);
    conversionFactorImportLineService.commitLines({ conversionSourceId, year })
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        getRecords('pending');
        setTab(0);
        setIsCommitting(false);
      })
      .catch((error) => {
        console.log(error)
        setIsCommitting(false);
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      })
  }

  const discardHandler = () => {
    conversionFactorImportLineService.discardLines({ conversionSourceId, year })
      .then(() => {
        getRecords('pending');
        userNotificationsContext.openSnackbar({ open: true, message: 'Discarded successfully' });
        setTab(0);
      })
      .catch((error) => {
        console.log(error)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      })
      .finally(() => {
        setIsDiscarding(false);
      });
  }

  return (
    <PageOuterContainer>
      <PageHeading title={`${tab === 0 ? 'Pending' : tab === 1 ? 'Committed' : 'Discarded'} Conversion Factors`} includeBackButton backButtonLink={`/super-admin/conversion-factors/${conversionSourceId}`} backButtonText='Back to Years' />
      <Box sx={{ borderBottom: 1, borderColor: 'lightGrey.main' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label='Pending'  {...a11yProps(0)} />
          <Tab label='Committed'  {...a11yProps(1)} />
          <Tab label='Discarded' {...a11yProps(2)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={tab} index={0}>
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {conversionSource && conversionSource.conversionType === 'conversion' &&
              <Button variant='outlined' sx={{ mr: 1 }} onClick={mapCategoryHandler} disabled={isCommitting || isDiscarding}>
                <Typography>Map categories</Typography>
              </Button>}
            <LoadingButton
              onClick={commitHandler}
              disabled={isCommitting || isDiscarding}
              startIcon={<SaveOutlined />}
              variant='contained'
              sx={{ mr: 1 }}
              loading={isCommitting}
              loadingPosition="start">
              <Typography>{isCommitting ? 'Committing' : 'Commit All'}</Typography>
            </LoadingButton>
            <LoadingButton
              onClick={discardHandler}
              disabled={isDiscarding || isCommitting}
              startIcon={<DeleteOutlined />}
              variant='text'
              loading={isDiscarding}
              loadingPosition="start">
              <Typography>{isDiscarding ? 'Discarding' : 'Discard All'}</Typography>
            </LoadingButton>
          </Box>
          {records && conversionSource && conversionSource.conversionType === 'conversion' &&
            <TableDisplay
              headCells={headCellsPending}
              rows={rows}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage}
              onOrderChange={(newOrderBy, newOrder) => {
                setOrderBy(newOrderBy);
                setOrder(newOrder);
              }}
            />}
          {records && conversionSource && conversionSource.conversionType === 'spendBasedConversion' && <TableDisplay
            headCells={headCellsPendingSpendBased}
            rows={rowsSpendBased}
            selected={selected}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage}
            onOrderChange={(newOrderBy, newOrder) => {
              setOrderBy(newOrderBy);
              setOrder(newOrder);
            }}
          />}
        </>
      </CustomTabPanel>
      <CustomTabPanel value={tab} index={1}>
        {records && conversionSource && conversionSource.conversionType === 'conversion' && <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
          onOrderChange={(newOrderBy, newOrder) => {
            setOrderBy(newOrderBy);
            setOrder(newOrder);
          }}
        />}
        {records && conversionSource && conversionSource.conversionType === 'spendBasedConversion' && <TableDisplay
          headCells={headCellsSpendBased}
          rows={rowsSpendBased}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
          onOrderChange={(newOrderBy, newOrder) => {
            setOrderBy(newOrderBy);
            setOrder(newOrder);
          }}
        />}
      </CustomTabPanel>
      <CustomTabPanel value={tab} index={2}>
        {records && conversionSource && conversionSource.conversionType === 'conversion' && <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
          onOrderChange={(newOrderBy, newOrder) => {
            setOrderBy(newOrderBy);
            setOrder(newOrder);
          }}
        />}
        {records && conversionSource && conversionSource.conversionType === 'spendBasedConversion' && <TableDisplay
          headCells={headCellsSpendBased}
          rows={rowsSpendBased}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
          onOrderChange={(newOrderBy, newOrder) => {
            setOrderBy(newOrderBy);
            setOrder(newOrder);
          }}
        />}
      </CustomTabPanel>
      {conversionSource && conversionSource.conversionType === 'conversion' && isMappingOpen &&
        <Dialog open={isMappingOpen} onClose={() => setIsMappingOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Map Level One to Category</DialogTitle>
          <DialogContent dividers>
            {levelOnes.map((lvl) => (
              <Box key={lvl} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ mr: 2, minWidth: 200 }}>{lvl}</Box>
                <FormControl fullWidth>
                  <InputLabel id={`cat-${lvl}`}>Category</InputLabel>
                  <Select
                    labelId={`cat-${lvl}`}
                    label='Category'
                    value={mappings[lvl] || ''}
                    onChange={(e) => setMappings(prev => ({ ...prev, [lvl]: e.target.value as string }))}>
                    {categories.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsMappingOpen(false)}>Cancel</Button>
            <Button variant='contained' disabled={!allMapped} onClick={saveDialogHandler}>Save</Button>
          </DialogActions>
        </Dialog>}
    </PageOuterContainer>
  );
}

